import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { SharedCombobox } from "@/components/shared/SharedCombobox";
import { ProductionThumbnailCell } from "@/components/shared/ProductionThumbnailCell";
import { trpcClient } from "@/lib/trpc";
import { formatNumber, formatDate } from "@/lib/utils";
import { Layers, Loader2, PackageCheck, AlertCircle, CheckSquare, Square } from "lucide-react";
import { toast } from "sonner";

interface CombineOrderModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
  customerName?: string;
  excludeOrderIds?: string[];
  alreadySelectedDetailIds?: string[];
  onCombine: (order: any, selectedDetailIds: string[]) => void;
}

export default function CombineOrderModal({
  open,
  onOpenChange,
  customerId,
  customerName,
  excludeOrderIds = [],
  alreadySelectedDetailIds = [],
  onCombine,
}: CombineOrderModalProps) {
  const [selectedOrderId, setSelectedOrderId] = useState<string>("");
  const [loadingOrder, setLoadingOrder] = useState<boolean>(false);
  const [orderDetails, setOrderDetails] = useState<any>(null);
  const [selectedDetailIds, setSelectedDetailIds] = useState<Set<string>>(new Set());

  // Reset state when modal opens or closes
  useEffect(() => {
    if (!open) {
      setSelectedOrderId("");
      setOrderDetails(null);
      setSelectedDetailIds(new Set());
    }
  }, [open]);

  // Load order details when an order is picked
  useEffect(() => {
    if (!selectedOrderId) {
      setOrderDetails(null);
      setSelectedDetailIds(new Set());
      return;
    }

    let isMounted = true;
    setLoadingOrder(true);
    trpcClient.orders.getById
      .query({ id: selectedOrderId })
      .then((order: any) => {
        if (!isMounted) return;
        setOrderDetails(order);

        const details = order?.orderDetails || order?.items || [];
        const initialSelected = new Set<string>();
        details.forEach((d: any) => {
          const isAlreadyIn = alreadySelectedDetailIds.includes(d.id);
          const orderQty = Number(d.quantity) || 0;
          const deliveredQty = Number(d.alreadyDelivered ?? d.qtyDelivered) || 0;
          const remaining = Math.max(0, orderQty - deliveredQty);
          // Auto-select items that are not already in form and have remaining quantity
          if (!isAlreadyIn && remaining > 0) {
            initialSelected.add(d.id);
          }
        });
        setSelectedDetailIds(initialSelected);
      })
      .catch((err: any) => {
        if (!isMounted) return;
        toast.error(`Không thể tải thông tin đơn hàng: ${err?.message || "Lỗi không xác định"}`);
      })
      .finally(() => {
        if (isMounted) setLoadingOrder(false);
      });

    return () => {
      isMounted = false;
    };
  }, [selectedOrderId, alreadySelectedDetailIds]);

  const details = orderDetails?.orderDetails || orderDetails?.items || [];
  const availableItems = details.filter((d: any) => !alreadySelectedDetailIds.includes(d.id));

  const toggleSelectAll = () => {
    if (selectedDetailIds.size === availableItems.length) {
      setSelectedDetailIds(new Set());
    } else {
      setSelectedDetailIds(new Set(availableItems.map((d: any) => d.id)));
    }
  };

  const toggleItem = (id: string) => {
    const next = new Set(selectedDetailIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedDetailIds(next);
  };

  const handleConfirmCombine = () => {
    if (!orderDetails || selectedDetailIds.size === 0) {
      toast.warning("Vui lòng chọn ít nhất một mặt hàng để gộp");
      return;
    }
    onCombine(orderDetails, Array.from(selectedDetailIds));
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col p-6 overflow-hidden">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="p-2 bg-blue-50 text-blue-700 rounded-lg">
              <Layers className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle>
                Gộp Thêm Đơn Hàng Cùng Khách Hàng
              </DialogTitle>
              <DialogDescription className="text-xs text-slate-500 mt-0.5">
                Chọn đơn hàng bổ sung của khách hàng{" "}
                <span className="font-semibold text-slate-700">{customerName || "hiện tại"}</span> để giao chung một chuyến
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-2">
          {/* Chọn đơn hàng */}
          <div className="space-y-1.5 bg-slate-50 p-3 rounded-lg border border-slate-200">
            <Label className="text-xs font-semibold text-slate-700">
              Chọn đơn hàng cần gộp:
            </Label>
            <SharedCombobox
              type="order"
              params={{ customerId: customerId || undefined }}
              value={selectedOrderId}
              onChange={(val) => {
                if (excludeOrderIds.includes(val)) {
                  toast.error("Đơn hàng này đã là đơn chính hoặc đã được chọn");
                  return;
                }
                setSelectedOrderId(val);
              }}
              placeholder="Tìm theo mã đơn hàng hoặc tên dự án..."
              disabled={!customerId}
            />
          </div>

          {/* Loading state */}
          {loadingOrder && (
            <div className="py-8 flex flex-col items-center justify-center text-slate-500 text-xs">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600 mb-2" />
              Đang tải danh sách sản phẩm từ đơn hàng...
            </div>
          )}

          {/* Danh sách sản phẩm của đơn hàng */}
          {!loadingOrder && orderDetails && (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-xs text-slate-600 bg-blue-50/50 p-2.5 rounded border border-blue-100">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-blue-900">Đơn hàng: {orderDetails.orderId}</span>
                  {orderDetails.orderDate && (
                    <span className="text-slate-500">({formatDate(orderDetails.orderDate)})</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={toggleSelectAll}
                    className="h-6 px-2 text-[11px] text-blue-700 hover:bg-blue-100"
                  >
                    {selectedDetailIds.size === availableItems.length ? (
                      <>
                        <Square className="h-3 w-3 mr-1" /> Bỏ chọn tất cả
                      </>
                    ) : (
                      <>
                        <CheckSquare className="h-3 w-3 mr-1" /> Chọn tất cả
                      </>
                    )}
                  </Button>
                  <span className="text-slate-400">|</span>
                  <span className="font-semibold text-blue-700">
                    Đã chọn: {selectedDetailIds.size} / {availableItems.length}
                  </span>
                </div>
              </div>

              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-700">
                      <TableHead className="w-10 text-center">Chọn</TableHead>
                      <TableHead className="min-w-[180px]">Sản phẩm</TableHead>
                      <TableHead className="w-16 text-center">ĐVT</TableHead>
                      <TableHead className="w-20 text-right">SL Đặt</TableHead>
                      <TableHead className="w-20 text-right">Đã Giao</TableHead>
                      <TableHead className="w-24 text-right text-rose-600 font-bold">Còn Thiếu</TableHead>
                      <TableHead className="w-24 text-right">Đơn Giá</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {details.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-6 text-slate-400 text-xs italic">
                          Đơn hàng không có sản phẩm nào
                        </TableCell>
                      </TableRow>
                    ) : (
                      details.map((d: any) => {
                        const isAlreadyIn = alreadySelectedDetailIds.includes(d.id);
                        const orderQty = Number(d.quantity) || 0;
                        const deliveredQty = Number(d.alreadyDelivered ?? d.qtyDelivered) || 0;
                        const remaining = Math.max(0, orderQty - deliveredQty);
                        const isChecked = selectedDetailIds.has(d.id);

                        const getNumber = (val: any) => {
                          if (val && typeof val === "object" && val.toString)
                            return parseFloat(val.toString());
                          return Number(val) || 0;
                        };
                        const price = getNumber(d.unitPrice) || getNumber(d.priceVND) || 0;

                        return (
                          <TableRow
                            key={d.id}
                            className={`border-b text-xs transition-colors cursor-pointer ${
                              isAlreadyIn
                                ? "bg-slate-50 opacity-60 cursor-not-allowed"
                                : isChecked
                                ? "bg-blue-50/40"
                                : "hover:bg-slate-50"
                            }`}
                            onClick={() => {
                              if (!isAlreadyIn) toggleItem(d.id);
                            }}
                          >
                            <TableCell className="text-center align-middle" onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={isChecked}
                                disabled={isAlreadyIn}
                                onCheckedChange={() => toggleItem(d.id)}
                              />
                            </TableCell>
                            <TableCell className="align-middle">
                              <div className="flex items-center gap-2.5">
                                <ProductionThumbnailCell
                                  imageUrl={d.product?.image}
                                  layoutUrl={d.printLayout?.fileUrl}
                                  title={d.productName || d.product?.productName}
                                  size={30}
                                  className="shrink-0"
                                />
                                <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                                  <div className="font-semibold text-slate-900 leading-tight">
                                    {d.productName || d.product?.productName || "—"}
                                  </div>
                                  {isAlreadyIn && (
                                    <Badge variant="secondary" className="text-[10px] mt-0.5 text-slate-500 w-fit">
                                      Đã có trong phiếu giao
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-center text-slate-600 align-middle">
                              {d.unit || d.product?.unit || "Hộp"}
                            </TableCell>
                            <TableCell className="text-right font-mono align-middle">
                              {formatNumber(orderQty)}
                            </TableCell>
                            <TableCell className="text-right font-mono text-slate-500 align-middle">
                              {formatNumber(deliveredQty)}
                            </TableCell>
                            <TableCell className="text-right font-mono font-bold text-rose-600 align-middle">
                              {formatNumber(remaining)}
                            </TableCell>
                            <TableCell className="text-right font-mono text-slate-700 align-middle">
                              {formatNumber(price)}₫
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {!selectedOrderId && !loadingOrder && (
            <div className="text-center py-10 text-slate-400 border border-dashed rounded-lg bg-slate-50/50">
              <Layers className="h-8 w-8 mx-auto mb-2 opacity-30 text-slate-500" />
              <p className="text-xs">Vui lòng chọn một đơn hàng ở trên để hiển thị danh sách sản phẩm</p>
            </div>
          )}
        </div>

        <DialogFooter className="border-t pt-3 flex items-center justify-between">
          <div className="text-xs text-slate-500">
            {selectedDetailIds.size > 0 && (
              <span>
                Sẽ thêm <strong className="text-blue-700">{selectedDetailIds.size}</strong> sản phẩm vào danh sách giao
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
            >
              Hủy
            </Button>
            <Button
              type="button"
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold"
              disabled={selectedDetailIds.size === 0 || loadingOrder}
              onClick={handleConfirmCombine}
            >
              <PackageCheck className="h-4 w-4 mr-1.5" /> Gộp Vào Phiếu Giao
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
